import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageBag({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Farm</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Bag</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The Bag tab is where you protect bag slots. A locked slot is never touched by the bot&rsquo;s
        irreversible actions &mdash; selling, melting, and using up a teleport item on a route. There
        is no switch here and nothing runs from this tab: it only decides what is safe. Everything is
        on the <strong className="text-white">FARM &rsaquo; Bag</strong> tab.
      </p>

      <Callout type="info">
        <strong>Why it has its own tab.</strong> The same locks protect items from{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>,{' '}
        <button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melt</button>{' '}
        and the{' '}
        <button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Autopilot</button>,
        so they live in one place instead of on one of those tabs.
      </Callout>

      <DocHeading>Locking a Slot</DocHeading>
      <Step n={1} title="Pick a bag tab">
        The numbered buttons at the top of the <strong>PROTECTED SLOTS</strong> card are the pages
        of your main bag (usually 1&ndash;8; the number follows your bag size once it has been read).
        <strong>P</strong> is the premium bag.
      </Step>
      <Step n={2} title="Hover before you click">
        Hovering a slot shows the item in it and whether it is protected:{' '}
        <em>LOCKED - never sold or melted</em> or <em>NOT protected - click to lock</em>.
      </Step>
      <Step n={3} title="Click to lock">
        The slot turns red. Click it again to unlock it. The change takes effect immediately.
      </Step>

      <Shot
        name="bag-01-protected-slots.png"
        tab="FARM › Bag"
        caption="Locked slots are red. Purple means an item is there but it is not protected."
        shoot="A FARM › Bag fül, a PROTECTED SLOTS kártya egy olyan táska-fülön, ahol vegyesen vannak piros (lezárt), lila (tárgy, nem védett) és üres rekeszek. Látszik a fül-választó sor (1–8 és P), a LOCK THIS TAB / UNLOCK THIS TAB / UNLOCK ALL gombok, a „Locked … on this page, … in total” sor, a rács alatti színmagyarázat, és alul a This tab / Locked sorok."
      />

      <DocHeading>Reading the Grid</DocHeading>
      <DocTable
        headers={['Colour', 'Meaning']}
        rows={[
          [<span className="text-red-400 font-semibold">Red</span>, 'locked - never sold or melted'],
          [<span className="text-brand-300 font-semibold">Purple</span>, 'an item sits here - NOT protected'],
          [<span className="text-gray-300 font-semibold">Dark</span>, 'empty slot'],
          [<span className="text-gray-500 font-semibold">Greyed out</span>, 'This slot does not exist in your bag. It cannot be clicked.'],
        ]}
      />
      <Callout type="warning">
        <strong>Purple is the colour that stands out, and it means NOT protected.</strong> Only red
        slots are safe. If in doubt, hover the slot: the second line of the tooltip says it plainly.
      </Callout>

      <DocHeading>The Controls</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<>Numbered tab buttons and <strong className="text-white">P</strong></>, 'Switch the page shown in the grid. Each page is 4 rows × 7 columns = 28 slots. There are as many numbered buttons as your main bag has pages (8 until the bag has been read). If your premium bag has more than one page, they read P1, P2 and so on.'],
          [<strong className="text-white">LOCK THIS TAB</strong>, 'Locks every slot on the page you are looking at.'],
          [<strong className="text-white">UNLOCK THIS TAB</strong>, 'Unlocks every slot on that page.'],
          [<strong className="text-white">UNLOCK ALL</strong>, 'Removes every lock on every tab. It asks first: the button turns into a red REALLY UNLOCK ALL, and only that one does it. It only asks when something is actually locked.'],
          [<><strong className="text-white">Locked</strong> (above the grid)</>, 'How many slots are locked on this page, and in total. A quick check that your locks are where you think they are.'],
          [<strong className="text-white">This tab</strong>, 'Which slots of which bag the page shows, and how many slots that bag has.'],
          [<><strong className="text-white">Locked</strong> (below the grid)</>, 'The total number of locked slots across every tab.'],
        ]}
      />

      <DocHeading>What a Lock Protects</DocHeading>
      <DocTable
        headers={['Bot action', 'With a locked slot']}
        rows={[
          [<button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Selling</button>, 'The slot is never even looked at, whatever the filters or item rules say.'],
          [<button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melting</button>, 'Same: nothing is melted from a locked slot, identified gear included.'],
          [<button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Teleport items on a route</button>, 'A teleport item in a locked slot is never used. If no copy of it sits in an unlocked slot, the route stops and tells you the teleport item is not in an unlocked bag slot. The route check may also name the slot: “bag 0” is the main bag, “bag 1” the premium bag, and slots are counted from 0, so slot 0 is cell 1 on tab 1 and slot 28 is cell 1 on tab 2.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        A lock does not lock the item in the game. You can still use, move or sell it yourself, and
        picking up loot is not affected at all.
      </p>
      <Callout type="info">
        <strong>The lock is on the slot, not on the item.</strong> Move an item out of a locked slot
        and it is no longer protected; put something into a locked slot and it is. Keep your keepers
        in a locked corner of the bag and the protection takes care of itself.
      </Callout>
      <Callout type="info">
        <strong>Stacks that get merged are covered for the whole pass.</strong> Gems, runes and
        materials can be merged by the game into another slot. So during one selling or melting
        pass, anything that sat in a locked slot when the pass began is left alone until the pass
        ends, even if it moved in the meantime.
      </Callout>

      <DocHeading>Messages</DocHeading>
      <DocTable
        headers={['Message', 'What it means']}
        rows={[
          [<strong className="text-white">(bag not read yet)</strong>, 'The bot has not read your bag yet, so the grid does not know what is where. It fills in by itself once a character is in the game.'],
          [<span className="text-amber-300">The game reports N slots here, less than 8 tabs x 28.</span>, 'Your main bag has fewer slots than eight full pages. The slots past the end are greyed out. Nothing to fix.'],
          [<span className="text-red-400">This bag has N slots - only the first 256 can be protected. Tell the developer.</span>, 'Your bag is larger than the protection can cover. Keep valuables in the first slots and report it.'],
          [<span className="text-amber-300">Too many locked slots to guard safely - nothing sold.</span>, 'A notice from Auto Sell (Auto Melt says “nothing melted”). More items sit in locked slots than one pass can keep track of, so the pass stops instead of risking a protected item. Unlock slots you do not need.'],
        ]}
      />

      <DocHeading>Good to Know</DocHeading>
      <DocTable
        headers={['Question', 'Answer']}
        rows={[
          ['Are locks saved?', 'Yes, with your bot settings. They are still there after a restart.'],
          ['Are locks per character?', 'No. They belong to this PC, not to a character: switch characters and the same slot numbers stay locked.'],
          ['Can I keep different locks for different kinds of farming?', <>Yes &mdash; a{' '}<button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">farm profile</button>{' '}saves your locks too, and asks before loading would unlock anything.</>],
          ['“It sold something I had locked.”', 'Hover that slot now. Most often the item was no longer in the locked slot (it had been moved), or the lock was on the other bag (main vs. P). Check the Locked total too: if it reads 0, nothing is locked.'],
        ]}
      />
    </div>
  );
}

export default PageBag;
